import { useEffect, useState } from "react";
import { getNotice, type ServiceNotice } from "../lib/galleryApi.ts";

/*
 * SystemNotice — the "the render computer is off" bar, shown at the top of every page of the
 * webmaster site.
 *
 * Why it exists: the site only ever shows what the owner's machine last pushed up. With the
 * machine switched off (a trip, a power cut) nothing changes, so a request the team submitted
 * looks like it silently vanished — which is exactly the complaint that prompted this. The bar
 * makes the outage explicit, says when the machine is expected back, and reassures that nothing
 * queued is lost.
 *
 * Two ways it lights up: automatically (no check-in from the machine for ~10 minutes) and from
 * the owner's own notice, which adds the exact expected-back time. It clears itself the moment
 * the machine checks in again.
 */

const POLL_MS = 30_000;

/** ISO → "Friday, September 12 at 6:00 PM AEST", in whatever timezone the READER is sitting in. */
function whenLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  return `${day} at ${time}`;
}

/** "25 minutes ago" / "3 hours ago" / "2 days ago" — how long the machine has been quiet. */
function agoText(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (!Number.isFinite(mins) || mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  return `${Math.round(hours / 24)} days ago`;
}

/** The one sentence that matters: is it off, and when is it coming back? */
function headline(n: ServiceNotice): string {
  const back = n.offUntil ? new Date(n.offUntil).getTime() : null;
  if (back !== null && back > Date.now()) return `The render computer is off. Expected back online ${whenLocal(n.offUntil!)}.`;
  if (back !== null) return `The render computer is still off. It was expected back ${whenLocal(n.offUntil!)}.`;
  return `The render computer is offline${n.lastSeen ? ` — last seen ${agoText(n.lastSeen)}` : ""}.`;
}

export default function SystemNotice() {
  const [notice, setNotice] = useState<ServiceNotice | null>(null);

  useEffect(() => {
    let alive = true;
    // A failed check (signed out, site restarting) must never invent an outage — stay silent.
    const load = () => getNotice().then((n) => alive && setNotice(n)).catch(() => alive && setNotice(null));
    load();
    const t = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (!notice || notice.online) return null;

  // Sits ABOVE each page's <main>, so it matches that column's width and padding itself.
  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "1.5rem 1.25rem 0" }}>
    <div
      role="status"
      style={{
        margin: 0,
        padding: "10px 14px",
        border: "1px solid #f59e0b",
        background: "rgba(245,158,11,0.14)",
        borderRadius: 8,
        fontSize: "0.88rem",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
      }}
    >
      <span aria-hidden style={{ fontSize: "1.05rem", lineHeight: 1.3 }}>🌙</span>
      <div>
        <strong>{headline(notice)}</strong>
        {notice.note && <div style={{ marginTop: 2 }}>{notice.note}</div>}
        <div className="muted" style={{ marginTop: 2 }}>
          Nothing is lost — anything you request now is saved and starts automatically when it's back on.
        </div>
      </div>
    </div>
    </div>
  );
}
